import type { CreateTreeReplacementInput } from '../../../../api/tree-replacements/types';
import type { WorkshopTreeReplacement } from '../../../../api/workshops';
import { formatInstantInVenueZone } from '../../../../utils/venueTime';

export const NOT_RECORDED_LABEL = 'Not recorded / unknown';

/** Trees to hand out, not customers, so the count reads like the seat counts. */
export function treesToHandOut(pickups: WorkshopTreeReplacement[]): number {
  return pickups.reduce((sum, p) => sum + (p.treeCount ?? 1), 0);
}

/** The staff note wins over the FareHarbor note; a row never shows both. */
export function displayedNote(pickup: WorkshopTreeReplacement): string | null {
  return pickup.staffNotes?.trim() || pickup.note;
}

/** First and last name when the API sends them, else the server's display name. */
export function replacementName(pickup: WorkshopTreeReplacement): string {
  const name = [pickup.firstName, pickup.lastName].filter(Boolean).join(' ').trim();
  return name || pickup.customerName;
}

/** "by Maya · Oct 14, 7:30 PM". The time shows only when the venue's zone is known. */
export function pickedUpLine(
  pickup: WorkshopTreeReplacement,
  venueTimeZone: string | null,
): string {
  const when = formatInstantInVenueZone(pickup.pickedUpAt, venueTimeZone, '');
  const who = pickup.pickedUpByName ? `by ${pickup.pickedUpByName}` : '';
  return [who, when].filter(Boolean).join(' · ');
}

export interface TreeReplacementFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  treeCount: string;
  /** Empty string means "not recorded". */
  treeSpeciesId: string;
  staffNotes: string;
}

export type TreeReplacementFormErrors = Partial<Record<keyof TreeReplacementFormValues, string>>;

export const TREE_REPLACEMENT_LIMITS = {
  name: 150,
  email: 320,
  phone: 50,
  notes: 2000,
  minTrees: 1,
  maxTrees: 10,
} as const;

export const EMPTY_TREE_REPLACEMENT_FORM: TreeReplacementFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  treeCount: '1',
  treeSpeciesId: '',
  staffNotes: '',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function treeCountError(raw: string): string | undefined {
  const n = Number(raw);
  const { minTrees, maxTrees } = TREE_REPLACEMENT_LIMITS;
  if (!Number.isInteger(n) || n < minTrees || n > maxTrees) {
    return `Enter a whole number from ${minTrees} to ${maxTrees}.`;
  }
  return undefined;
}

export function validateTreeReplacementForm(
  values: TreeReplacementFormValues,
  { includeContact }: { includeContact: boolean },
): TreeReplacementFormErrors {
  const errors: TreeReplacementFormErrors = {};
  if (includeContact) {
    if (values.firstName.trim().length === 0) errors.firstName = 'Enter a first name.';
    const email = values.email.trim();
    if (email && !EMAIL_PATTERN.test(email)) errors.email = 'Enter a valid email address.';
  }
  const count = treeCountError(values.treeCount);
  if (count) errors.treeCount = count;
  return errors;
}

function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function toCreateInput(values: TreeReplacementFormValues): CreateTreeReplacementInput {
  return {
    firstName: values.firstName.trim(),
    lastName: optional(values.lastName),
    email: optional(values.email),
    phone: optional(values.phone),
    treeCount: Number(values.treeCount),
    treeSpeciesId: values.treeSpeciesId || null,
    staffNotes: optional(values.staffNotes),
  };
}

const FORM_FIELDS: readonly (keyof TreeReplacementFormValues)[] = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'treeCount',
  'treeSpeciesId',
  'staffNotes',
];

/** The server's per-field messages that belong to a field on this form. */
export function formErrorsFromServer(
  fieldErrors: Record<string, string>,
): TreeReplacementFormErrors {
  const errors: TreeReplacementFormErrors = {};
  for (const key of FORM_FIELDS) {
    if (fieldErrors[key]) errors[key] = fieldErrors[key];
  }
  return errors;
}
